import { Component, OnInit, OnDestroy, signal, inject, computed } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ApiService } from '../../../core/service/api-service/api-service';
import { API } from '../../../core/API';

@Component({
  selector: 'app-history-events-list',
  standalone: false,
  templateUrl: './history-events-list.component.html',
  styleUrls: ['./history-events-list.component.css']
})
export class HistoryEventsListComponent implements OnInit, OnDestroy {
  private api = inject(ApiService);
  private destroy$ = new Subject<void>();

  events        = signal<any[]>([]);
  isLoading     = signal(true);
  errorMessage  = signal<string | null>(null);
  searchQuery   = signal('');
  selectedStatus = signal('');

  filteredEvents = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const s = this.selectedStatus();
    return this.events().filter(e => {
      const matchStatus = !s || (e.status || 'ACTI') === s;
      const matchSearch = !q ||
        (e.title || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        String(e.year || '').includes(q);
      return matchStatus && matchSearch;
    });
  });

  private searchSubject = new Subject<string>();

  // Add / edit form
  showForm     = signal(false);
  editingEvent = signal<any>(null);
  isSaving     = signal(false);
  formData     = signal({ year: new Date().getFullYear(), title: '', description: '' });

  // Status confirmation
  pendingStatus = signal<{ id: number; status: 'ACTI' | 'INAC' } | null>(null);

  // Archive confirmation
  pendingArchive = signal<number | null>(null);

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(q => this.searchQuery.set(q));
    this.loadEvents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  onSearch(value: string): void { this.searchSubject.next(value); }
  onStatusFilter(value: string): void { this.selectedStatus.set(value); }

  loadEvents(): void {
    this.isLoading.set(true);
    this.api.get(API.HISTORY_EVENTS.GET_ALL)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (r: any) => { this.events.set(r.data || []); this.isLoading.set(false); },
        error: () => { this.errorMessage.set('Erreur lors du chargement'); this.isLoading.set(false); }
      });
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  openAddForm(): void {
    this.editingEvent.set(null);
    this.formData.set({ year: new Date().getFullYear(), title: '', description: '' });
    this.errorMessage.set(null);
    this.showForm.set(true);
  }

  openEditForm(event: any): void {
    this.editingEvent.set(event);
    this.formData.set({ year: event.year || new Date().getFullYear(), title: event.title || '', description: event.description || '' });
    this.errorMessage.set(null);
    this.showForm.set(true);
  }

  closeForm(): void { this.showForm.set(false); this.editingEvent.set(null); this.errorMessage.set(null); }

  updateField(field: string, value: any): void { this.formData.set({ ...this.formData(), [field]: value }); }

  onSave(): void {
    if (!this.formData().title.trim()) { this.errorMessage.set('Le titre est requis'); return; }
    if (!this.formData().year) { this.errorMessage.set('L\'année est requise'); return; }
    this.isSaving.set(true);
    this.errorMessage.set(null);
    const body = { year: this.formData().year, title: this.formData().title, description: this.formData().description };
    const editing = this.editingEvent();
    const req$ = editing
      ? this.api.put(`${API.HISTORY_EVENTS.UPDATE}/${editing.id_event}`, body)
      : this.api.post(API.HISTORY_EVENTS.ADD, body);
    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.isSaving.set(false); this.closeForm(); this.loadEvents(); },
      error: () => { this.errorMessage.set('Erreur lors de l\'enregistrement'); this.isSaving.set(false); }
    });
  }

  // ── Status ────────────────────────────────────────────────────────────────
  confirmStatus(event: any, status: 'ACTI' | 'INAC', $event: MouseEvent): void {
    $event.stopPropagation();
    this.pendingStatus.set({ id: event.id_event, status });
  }
  cancelStatus(): void { this.pendingStatus.set(null); }
  applyStatus(): void {
    const p = this.pendingStatus();
    if (!p) return;
    this.pendingStatus.set(null);
    this.api.patch(`${API.HISTORY_EVENTS.SET_STATUS}/${p.id}`, { status: p.status })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.events.update(list => list.map(e => e.id_event === p.id ? { ...e, status: p.status } : e)),
        error: () => this.errorMessage.set('Erreur lors du changement de statut')
      });
  }

  // ── Archive ───────────────────────────────────────────────────────────────
  confirmArchive(id: number, $event: MouseEvent): void {
    $event.stopPropagation();
    this.pendingArchive.set(id);
  }
  cancelArchive(): void { this.pendingArchive.set(null); }
  applyArchive(): void {
    const id = this.pendingArchive();
    if (!id) return;
    this.pendingArchive.set(null);
    this.api.delete(`${API.HISTORY_EVENTS.DELETE}/${id}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadEvents(),
        error: () => this.errorMessage.set('Erreur lors de l\'archivage')
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  statusLabel(status: string): string { return status === 'ACTI' ? 'En ligne' : 'Hors ligne'; }
  statusBadgeClass(status: string): string {
    return status === 'ACTI'
      ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
      : 'bg-amber-50 text-amber-600 border-amber-100';
  }
}
